import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { adminApi } from "@/lib/lnlApi";
import { useAuth } from "@/lib/lnlAuth";
import { Modal, EmailFrame, errMsg, inputCls } from "./shared";

export default function PreviewDialog({ template, canTest, onClose }) {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [email, setEmail] = useState(user?.email || "");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        adminApi.post("/auto-responses/preview", {
            subject: template.subject, body: template.body,
            signature_id: template.signature_id, attachment_media_ids: template.attachment_media_ids,
        }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    }, [template]);

    async function sendTest() {
        setBusy(true);
        try { const { data: r } = await adminApi.post(`/auto-responses/templates/${template.id}/test-send`, { email }); toast.success(`Test email sent to ${r.to}`); }
        catch (e) { toast.error(errMsg(e)); }
        finally { setBusy(false); }
    }

    return (
        <Modal title={`Preview — ${template.template_name}`} onClose={onClose} wide testid="preview-dialog">
            {!data ? <div className="text-sm text-lnl-gray">Rendering preview…</div> : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2 space-y-3">
                        <div className="rounded-md border border-lnl-border p-3 bg-lnl-off-white">
                            <div className="text-[11px] uppercase tracking-widest text-lnl-gray font-heading font-semibold">Email Subject</div>
                            <div className="font-heading font-semibold text-lnl-charcoal mt-1" data-testid="preview-subject">{data.subject}</div>
                        </div>
                        <EmailFrame html={data.html} testid="preview-body" />
                    </div>
                    <aside className="space-y-4">
                        <div>
                            <div className="text-[11px] uppercase tracking-widest text-lnl-gray font-heading font-semibold">Variables in this template</div>
                            <ul className="mt-2 space-y-1.5 text-xs" data-testid="preview-variables">
                                {data.variables.length === 0 && <li className="text-lnl-gray">No variables used.</li>}
                                {data.variables.map((v) => (
                                    <li key={v.name} className="flex flex-col rounded border border-lnl-border px-2 py-1.5">
                                        <code className={v.known ? "text-lnl-green-deep" : "text-red-600"}>{`{{${v.name}}}`}</code>
                                        <span className="text-lnl-charcoal">{v.known ? (v.value || "(empty)") : "Unknown variable — will be removed"}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {canTest && template.id && (
                            <div className="rounded-md border border-lnl-border p-3">
                                <div className="font-heading font-semibold text-sm">Send Test Email</div>
                                <p className="text-[11px] text-lnl-gray mt-1">Uses sample data. Must be an LNL staff account email. Subject will start with “TEST EMAIL - LNL Automated Response”.</p>
                                <input value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-2`} data-testid="test-email-input" />
                                <button onClick={sendTest} disabled={busy || !email} className="mt-2 lnl-btn-primary w-full justify-center" data-testid="test-email-send"><Send className="h-4 w-4" /> {busy ? "Sending…" : "Send Test Email"}</button>
                            </div>
                        )}
                    </aside>
                </div>
            )}
        </Modal>
    );
}
